import { ReactNode } from "react";

import { ChevronLeftIcon, ChevronRightIcon, PhotoIcon } from "@heroicons/react/20/solid";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { ReturnRow } from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { INBOX_DAY } from "../../utils/inboxDates";
import PriceFormat from "../../utils/priceFormat";
import ChannelTag from "./ChannelTag";
import DeadlineChip from "./DeadlineChip";
import { LargeItemPill } from "./LargeItem";
import ReasonCell from "./ReasonCell";
import StageChip from "./StageChip";
import StateChip from "./StateChip";

interface ReturnsTableProps {
  rows: ReturnRow[];
  now: DateTime;
  onOpen: (id: string) => void;
  page: number;
  totalPages: number;
  total: number;
  onPage: (page: number) => void;
  /** "9 open returns · 11 in the last 90 days with closed". */
  summary: string;
  loading?: boolean;
  /** Shown in place of the rows when there are none. */
  empty?: ReactNode;
}

/** What the deadline column shows when no eBay deadline applies to the row. */
function deadlineFallback(row: ReturnRow, t: (key: string, options?: Record<string, unknown>) => string) {
  if (row.partialOffer) return { text: t("Buyer to respond"), sub: row.partialOffer.at ? t("Offered {{when}}", { when: formatDateTime(row.partialOffer.at, INBOX_DAY) }) : undefined };
  if (row.expectedAt) return { text: t("Expected {{when}}", { when: formatDateTime(row.expectedAt, INBOX_DAY) }), sub: row.trackingSummary ?? undefined };
  if (row.trackingSummary) return { text: row.trackingSummary };
  return { text: "—" };
}

/**
 * The returns list, our own table rather than PagedTable: rows need the deadline chip, two-line
 * cells and a red edge when overdue. Paging is the server's.
 */
export default function ReturnsTable({ rows, now, onOpen, page, totalPages, total, onPage, summary, loading = false, empty }: ReturnsTableProps) {
  const { t } = useTranslation("returns");

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2.5 border-b border-gray-200 bg-white px-3.5 py-2 text-[12px]">
        <span className="ml-auto text-gray-500">{t("Sorted by nearest eBay deadline")}</span>
      </div>
      {rows.length === 0 && !loading ? (
        <div className="p-4">{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className={classNames("w-full min-w-[960px] border-collapse text-[12px]", loading && "opacity-60")}>
            <thead>
              <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <th className="py-2 pl-3.5 pr-2.5 font-semibold">{t("Part")}</th>
                <th className="px-2.5 py-2 font-semibold">{t("Order · buyer")}</th>
                <th className="px-2.5 py-2 font-semibold">{t("eBay reason")}</th>
                <th className="px-2.5 py-2 font-semibold">{t("eBay state")}</th>
                <th className="px-2.5 py-2 font-semibold">{t("Wilma stage")}</th>
                <th className="px-2.5 py-2 text-right font-semibold">{t("Est. refund")}</th>
                <th className="py-2 pl-2.5 pr-3.5 font-semibold">{t("Deadline now")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                return (
                  <tr
                    key={row.id}
                    onClick={() => onOpen(row.id)}
                    className={classNames("cursor-pointer border-t border-gray-100 hover:bg-gray-50", row.overdue && "shadow-[inset_3px_0_0_#B91C1C]")}
                  >
                    <td className="py-2 pl-3.5 pr-2.5">
                      <div className="flex items-center gap-2">
                        {row.imageUrl ? (
                          <img src={row.imageUrl} alt="" className="h-9 w-9 shrink-0 rounded-md bg-gray-200 object-cover" />
                        ) : (
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gray-200 text-gray-400">
                            <PhotoIcon className="h-4 w-4" />
                          </span>
                        )}
                        <span className="flex min-w-0 flex-col">
                          <span className="max-w-[240px] truncate font-semibold text-gray-900">{row.partTitle ?? t("Part not linked")}</span>
                          <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                            {row.tagNumber ?? "—"}
                            {row.largeItem && <LargeItemPill item={row.largeItem} />}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className="px-2.5 py-2">
                      <div className="flex flex-col">
                        <span className="flex items-center gap-1.5">
                          <ChannelTag channel={row.channel} />
                          {row.buyer ?? "—"}
                        </span>
                        <span className="font-mono text-[11px] text-gray-500">{row.orderNumber ?? "—"}</span>
                      </div>
                    </td>
                    <td className="px-2.5 py-2">
                      <ReasonCell {...row} />
                    </td>
                    <td className="px-2.5 py-2">
                      <StateChip state={row.ebayState} label={row.ebayStateLabel} />
                    </td>
                    <td className="px-2.5 py-2">
                      <StageChip stage={row.stage} label={row.stageLabel} by={row.stageBy} />
                    </td>
                    <td className="px-2.5 py-2 text-right font-semibold text-gray-900">
                      {row.partialOffer ? (
                        <>
                          {PriceFormat(row.partialOffer.amount)} <span className="font-normal text-gray-500">{t("of {{amount}}", { amount: PriceFormat(row.partialOffer.of) })}</span>
                        </>
                      ) : row.estimatedRefund ? (
                        PriceFormat(row.estimatedRefund.total)
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-2 pl-2.5 pr-3.5">
                      <DeadlineChip deadline={row.deadline} now={now} fallback={deadlineFallback(row, t)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-3.5 py-2 text-[12px] text-gray-500">
        <span>{summary}</span>
        <span className="flex items-center gap-2">
          {totalPages > 1 && (
            <span className="flex items-center gap-1">
              <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label={t("Previous page")} className="rounded-md border border-gray-200 p-1 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <span>{t("Page {{page}} of {{total}}", { page, total: totalPages })}</span>
              <button type="button" onClick={() => onPage(page + 1)} disabled={page >= totalPages} aria-label={t("Next page")} className="rounded-md border border-gray-200 p-1 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </span>
          )}
          <span>{t("Deadlines are eBay's own, shown in your local time")}</span>
        </span>
      </div>
      {total === 0 && loading && <div className="p-4 text-[12px] text-gray-500">{t("Loading returns…")}</div>}
    </section>
  );
}
